import { useTranslation } from 'react-i18next';
import { ArrowRight, Lock } from 'lucide-react';
import { isMaskedChange, type AuditChange } from 'api/adminAuditApi';
import { enumValueLabel, fieldLabel, maskedValueLabel } from 'utils/auditLabels';
import Pill from './Pill';
import ValueCell from './ValueCell';

/** Before → after separator for one diff row — inlined, single caller (ponytail). */
const Arrow = () => <ArrowRight className="mt-0.5 h-[14px] w-[14px] shrink-0 self-start text-muted-foreground/70" aria-hidden="true" />;

/** Same list, diffed: removed items struck through, added ones prefixed "+", the rest neutral (CA6).
 * A plain helper, not a component — it returns a pair of nodes, never mounted as JSX itself. */
const diffList = (before: unknown[], after: unknown[], label: (item: string) => string) => {
  const from = before.map(String);
  const to = after.map(String);
  return {
    beforePills:
      from.length === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="flex flex-wrap gap-1.5">
          {from.map((item, index) => (
            <Pill key={`${item}-${index}`} tone={to.includes(item) ? 'neutral' : 'removed'}>
              {label(item)}
            </Pill>
          ))}
        </span>
      ),
    afterPills:
      to.length === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="flex flex-wrap gap-1.5">
          {to.map((item, index) => (
            <Pill key={`${item}-${index}`} tone={from.includes(item) ? 'neutral' : 'added'}>
              {label(item)}
            </Pill>
          ))}
        </span>
      ),
  };
};

/** One field's before → after (J3, CA6, CA25/CA26 via the shared label helpers). */
const ChangeRow = ({ field, change }: { field: string; change: AuditChange }) => {
  const { t } = useTranslation();
  const label = fieldLabel(t, field);

  if (isMaskedChange(change)) {
    return (
      <>
        <span className="font-medium">{label}</span>
        <span className="col-span-3 inline-flex items-center gap-1.5 italic text-muted-foreground">
          <Lock className="h-[13px] w-[13px] shrink-0" aria-hidden="true" />
          {maskedValueLabel(t)}
        </span>
      </>
    );
  }

  // Creation — no `from` at all: only the value that landed is shown (task brief §1).
  if (!('from' in change)) {
    return (
      <>
        <span className="font-medium">{label}</span>
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <ValueCell field={field} value={change.to} bold />
      </>
    );
  }

  if (Array.isArray(change.from) || Array.isArray(change.to)) {
    const { beforePills, afterPills } = diffList(
      Array.isArray(change.from) ? change.from : [],
      Array.isArray(change.to) ? change.to : [],
      (item) => enumValueLabel(t, field, item)
    );
    return (
      <>
        <span className="font-medium">{label}</span>
        {beforePills}
        <Arrow />
        {afterPills}
      </>
    );
  }

  return (
    <>
      <span className="font-medium">{label}</span>
      <ValueCell field={field} value={change.from} strike />
      <Arrow />
      <ValueCell field={field} value={change.to} bold />
    </>
  );
};

export default ChangeRow;
